'use client';

import * as React from 'react';
import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useClick,
  useDismiss,
  useInteractions,
} from '@floating-ui/react';
import { cn } from '../lib/utils';
import { Button } from './button';
import { Calendar } from './calendar';

export interface DatePickerProps {
  /** The selected date (native Date object) */
  date: Date | undefined;
  /** Callback when date changes */
  onDateChange: (date: Date | undefined) => void;
  /** Placeholder text for the input */
  placeholder?: string;
  /** Number of months to display (default: 1) */
  numberOfMonths?: number;
  /** Function to determine if a date should be disabled */
  disabled?: (date: Date) => boolean;
  /** Optional className for the trigger button */
  className?: string;
  /** Unique identifier */
  id?: string;
  /** Date format string (default: 'PPP' e.g., "January 1, 2024") */
  dateFormat?: string;
}

function DatePicker({
  date,
  onDateChange,
  placeholder = 'Pick a date',
  numberOfMonths = 1,
  disabled,
  className,
  id,
  dateFormat = 'PPP',
}: DatePickerProps) {
  const [isOpen, setIsOpen] = React.useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset(4), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
    placement: 'bottom-start',
    strategy: 'absolute',
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  const handleSelect = (selectedDate: Date | undefined) => {
    onDateChange(selectedDate);
    setIsOpen(false);
  };

  return (
    <div
      style={{
        position: 'relative',
        display: 'inline-block',
      }}
    >
      <Button
        id={id}
        ref={refs.setReference}
        variant="outline"
        className={cn(
          'w-[280px] justify-start text-left font-normal',
          !date && 'text-muted-foreground',
          className,
        )}
        {...getReferenceProps()}
      >
        <CalendarIcon className="mr-2 h-4 w-4" />
        {date ? format(date, dateFormat) : <span>{placeholder}</span>}
      </Button>
      {isOpen && (
        <div
          ref={refs.setFloating}
          style={{
            position: floatingStyles.position,
            top: floatingStyles.top,
            left: floatingStyles.left,
            transform: floatingStyles.transform,
            zIndex: 9999,
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '0.375rem',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            padding: 0,
          }}
          {...getFloatingProps()}
        >
          <Calendar
            mode="single"
            selected={date}
            onSelect={handleSelect}
            numberOfMonths={numberOfMonths}
            disabled={disabled}
            initialFocus
          />
        </div>
      )}
    </div>
  );
}
DatePicker.displayName = 'DatePicker';

export { DatePicker };
